import type { DeleteSiteSectionProgrammingRangeCommand } from "@/domains/v4b/site-programming-range/site-programming-range.types";
import type { TDateTime } from "@/utils/time";

import { isDefined } from "@voltalis/frontend-utils";
import { computed, ref } from "vue";

import { isProgrammingRangePassed } from "@/components/piloting/planning/section-programming-ranges-utils";
import { useDeleteSiteProgrammingRangeMutation } from "@/composables/kili/site-programming-range/mutations/use-delete-site-programming-range-mutation";
import { useToaster } from "@/composables/layout/use-toaster";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { ProgramId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

interface ProgrammingRangeDeletionRequest {
  command: DeleteSiteSectionProgrammingRangeCommand;
  programmingRangeEnd?: TDateTime | null;
  programId: ProgramId;
}

export function useProgrammingRangeDeletion() {
  const { t, k } = useTypedIntl();
  const toast = useToaster();
  const { mutateAsync: deleteProgrammingRange, isPending: isDeletionPending } = useDeleteSiteProgrammingRangeMutation();
  const requestForDeletion = ref<ProgrammingRangeDeletionRequest>();
  const isConfirmationDialogVisible = computed({
    get: () => isDefined(requestForDeletion.value),
    set: (isVisible: boolean) => {
      if (isVisible) return;
      requestForDeletion.value = undefined;
    },
  });

  function isDeletionAllowed(request: ProgrammingRangeDeletionRequest): boolean {
    return !isProgrammingRangePassed({ end: request.programmingRangeEnd }, new Date());
  }

  function requestDeletion(request: ProgrammingRangeDeletionRequest) {
    if (isDeletionPending.value || !isDeletionAllowed(request)) return;

    requestForDeletion.value = request;
  }

  async function confirmDeletion() {
    if (isDeletionPending.value) return;

    const request = requestForDeletion.value;
    if (!request) return;

    if (!isDeletionAllowed(request)) {
      isConfirmationDialogVisible.value = false;
      return;
    }

    try {
      await deleteProgrammingRange(request.command);

      toast.add({
        severity: "success",
        detail: t(k.control.piloting.planning.deleteProgrammingRange.success),
      });

      isConfirmationDialogVisible.value = false;

      MixpanelService.trackEvent(EMixpanelEvent.PROGRAMMING_RANGE_DELETED, {
        space_id: request.command.sectionId,
        program_id: request.programId,
      });
    } catch (error) {
      toastKiliError(error, "control.piloting.planning.deleteProgrammingRange.error");
    }
  }

  return {
    isConfirmationDialogVisible,
    isDeletionPending,
    requestDeletion,
    confirmDeletion,
  };
}
